import React, { useState, useRef, useMemo, useEffect, useLayoutEffect } from 'react'
import { Flex, Box, Text, ArrowForwardIcon, ArrowBackIcon } from 'vvs-uikit'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { QUEST_ITEMS_PER_PAGE } from 'config/constants/vvsgotchi'
import { useTranslation } from 'contexts/Localization'
import { QuestHistoryWithConfig } from 'state/vvsgotchi/models'
import useQuestHistoryRecords from 'state/vvsgotchi/hooks/useQuestHistoryRecords'
import { formatTime } from 'utils/dayjs'
import { ListWrapper, ArrowArea, InfoGroup, StyledRow } from './styles'
import QuestStatItem from '../QuestStatItem'
import QuestHistoryEmpty from './QuestHistoryEmpty'

const QuestHistoryList: React.FC = () => {
  const { account } = useActiveWeb3React()
  const { records, loading, fetchNext, fetchedPage, totalPages } = useQuestHistoryRecords()

  const { t } = useTranslation()

  const [page, setPage] = useState(0)
  const initPage = useRef(true)
  const pageControlRef = useRef<HTMLDivElement>()

  const pagedRecords = useMemo(() => {
    return records.slice(QUEST_ITEMS_PER_PAGE * page, (page + 1) * QUEST_ITEMS_PER_PAGE)
  }, [records, page])

  useEffect(() => {
    if (page >= fetchedPage) {
      fetchNext()
    }
  }, [page, fetchedPage, fetchNext])

  useEffect(() => {
    initPage.current = true
    setPage(0)
  }, [account])

  // keep scroll at bottom after switching page
  useLayoutEffect(() => {
    if (!initPage.current && pageControlRef.current) {
      pageControlRef.current.scrollIntoView()
    }
  }, [loading, page])

  if (!loading && records.length === 0) return <QuestHistoryEmpty />

  return (
    <ListWrapper>
      {loading ? (
        <Flex flex="1" justifyContent="center" alignItems="center">
          <Text color="black">{t('Loading...')}</Text>
        </Flex>
      ) : (
        <Box flex="1">
          {pagedRecords.map((quest) => (
            <RecordRow key={quest.index} quest={quest} />
          ))}
        </Box>
      )}
      <Flex ref={pageControlRef} mt="0.2em" mb="1.2em" alignItems="center" justifyContent="space-between">
        <Text color="black" px="20px" bold>
          {pagedRecords?.length > 0 &&
            t('#%start%-#%end%', {
              start: pagedRecords[0].index + 1,
              end: pagedRecords[pagedRecords.length - 1].index + 1,
            })}
        </Text>
        <Flex alignItems="center">
          <ArrowArea
            onClick={() => {
              if (page > 0 || loading) {
                initPage.current = false
                setPage(page - 1)
              }
            }}
          >
            <ArrowBackIcon color={page <= 0 || loading ? 'textSubtle' : 'primary'} />
          </ArrowArea>
          {totalPages > 0 && (
            <Text color="black" bold>
              {t('Page %page% of %totalPages%', {
                page: page + 1,
                totalPages,
              })}
            </Text>
          )}
          <ArrowArea
            onClick={async () => {
              if (page >= totalPages - 1 || loading) return
              initPage.current = false
              setPage(page + 1)
            }}
          >
            <ArrowForwardIcon color={page >= totalPages - 1 || loading ? 'textSubtle' : 'primary'} />
          </ArrowArea>
        </Flex>
      </Flex>
    </ListWrapper>
  )
}

export default QuestHistoryList

const RecordRow: React.FC<{ quest: QuestHistoryWithConfig }> = ({ quest }) => {
  return (
    <StyledRow>
      <InfoGroup>
        <Text color="black" bold>
          {quest.name}
        </Text>
        <Text color="darkGrey" small>
          {quest?.description}
        </Text>
        {quest.completedAt && (
          <Text color="black" small>
            {formatTime(quest.completedAt, 'DD.MM.YYYY HH:mm')} UTC
          </Text>
        )}
      </InfoGroup>
      <QuestStatItem type="exp" value={quest.earnedExp} valuePrefix="+" justifyContent="flex-end" />
    </StyledRow>
  )
}
